import { additionalCandlePatterns } from './candlestick-patterns.mjs';
import { patternEnabled } from './pattern-catalog.mjs';
// Deterministic visual pattern candidates; never trading signals.
export function scanPatterns(source, p1, p2, settings={}) {
    if (!p1 || !p2) return { candles: 0, patterns: [] };
    const [start,end] = [p1[0],p2[0]].sort((a,b)=>a-b);
    const [low,high] = [p1[1],p2[1]].sort((a,b)=>a-b);
    const rows = (source || []).filter(c => c.length >= 5 && c.slice(0,5).every(Number.isFinite) &&
        c[0]>=start && c[0]<=end && c[3]>=low && c[2]<=high && c[2]>=Math.max(c[1],c[4]) && c[3]<=Math.min(c[1],c[4]));
    const patterns=[];
    const add=(name,cs,points,neckline=[],pivots=[])=>{
        if(!patternEnabled(name,settings))return null;
        const pattern={name,kind:points?'chart':'candlestick',candles:cs.map(c=>c.slice(0,5)),points:points || cs.map(c=>[c[0],c[2]]),neckline,pivots,start:cs[0][0],end:cs[cs.length-1][0]};
        patterns.push(pattern);return pattern;
    };
    const indices=new Map((source || []).map((c,i)=>[c,i]));
    const consecutive=(a,b)=>indices.get(b)===indices.get(a)+1;
    rows.forEach((c,i)=>{
        const [,o,h,l,close]=c, range=h-l, body=Math.abs(close-o);
        if(range<=0)return;
        const upper=h-Math.max(o,close),lower=Math.min(o,close)-l;
        if(body<=range*.1) add('Doji',[c]);
        else if(lower>=body*2 && upper<=range*.15) add('Hammer / hanging man shape',[c]);
        else if(upper>=body*2 && lower<=range*.15) add('Inverted hammer / shooting star shape',[c]);
        else if(body>=range*.9) add(close>o?'Bullish marubozu':'Bearish marubozu',[c]);
        else if(body<=range*.3 && upper>=range*.2 && lower>=range*.2) add('Spinning top',[c]);
        const p=rows[i-1];
        if(!p || !consecutive(p,c))return;
        if(close>o && p[4]<p[1] && o<=p[4] && close>=p[1])add('Bullish engulfing',[p,c]);
        if(close<o && p[4]>p[1] && o>=p[4] && close<=p[1])add('Bearish engulfing',[p,c]);
        if(h<p[2] && l>p[3])add('Inside bar',[p,c]);
        if(h>p[2] && l<p[3])add('Outside bar',[p,c]);
        const a=rows[i-2];
        if(!a || !consecutive(a,p))return;
        const ab=Math.abs(a[4]-a[1]),pb=Math.abs(p[4]-p[1]);
        if(ab>0 && pb<ab*.35 && a[4]<a[1] && close>o && close>(a[1]+a[4])/2 && Math.max(p[1],p[4])<a[1])add('Morning star candidate',[a,p,c]);
        if(ab>0 && pb<ab*.35 && a[4]>a[1] && close<o && close<(a[1]+a[4])/2 && Math.min(p[1],p[4])>a[1])add('Evening star candidate',[a,p,c]);
    });
    // Split at excluded candles: a formation must not bridge outside the box.
    const runs=[];
    rows.forEach(c=>{if(!runs.length || !consecutive(runs[runs.length-1].at(-1),c))runs.push([]);runs.at(-1).push(c);});
    for(const run of runs){
        const firstIndex=indices.get(run[0]);
        additionalCandlePatterns(run,(name,candles)=>add(name,candles),{prior:source.slice(Math.max(0,firstIndex-5),firstIndex),requireTrend:settings.requirePatternTrend!==false});
        const pivots=[];
        const strength=Math.max(2,Math.min(10,Math.floor(Number(settings.patternPivotStrength)||2)));
        for(let i=strength;i<run.length-strength;i++){
            const c=run[i],near=run.slice(i-strength,i+strength+1);
            const top=near.every(x=>x===c || x[2]<c[2]);
            const bottom=near.every(x=>x===c || x[3]>c[3]);
            if(top===bottom)continue;
            const pivot={i,t:c[0],v:top?c[2]:c[3],top};
            const last=pivots.at(-1);
            if(last && last.top===top){if(top?pivot.v>last.v:pivot.v<last.v)pivots[pivots.length-1]=pivot;}
            else pivots.push(pivot);
        }
        const emit=(name,ps,neck=[],extra={})=>{const pattern=add(name,run.slice(ps[0].i,ps.at(-1).i+1),ps.map(p=>[p.t,p.v]),neck.map(p=>[p.t,p.v]),ps.map(p=>({time:p.t,price:p.v,kind:p.top?'high':'low'})));if(pattern)Object.assign(pattern,extra);};
        for(let i=0;i<pivots.length;i++){
            const three=pivots.slice(i,i+3);
            if(three.length===3){
                const [a,b,c]=three, depth=Math.abs((a.v+c.v)/2-b.v);
                if(depth>0 && Math.abs(a.v-c.v)<=depth*.2)emit(a.top?'Double top':'Double bottom',three,[{t:a.t,v:b.v},{t:c.t,v:b.v}]);
            }
            const five=pivots.slice(i,i+5);
            if(five.length!==5)continue;
            const [a,b,c,d,e]=five,sign=a.top?1:-1;
            const depth=sign*(c.v-(b.v+d.v)/2);
            if(depth>0 && (sign*c.v-Math.max(sign*a.v,sign*e.v))>depth*.15 && Math.abs(a.v-e.v)<depth*.25 && Math.abs(b.v-d.v)<depth*.3 &&
                Math.min(c.i-a.i,e.i-c.i)/Math.max(c.i-a.i,e.i-c.i)>=.4)emit(a.top?'Head and shoulders':'Inverse head and shoulders',five,[b,d]);
            const tops=five.filter(p=>p.top),bottoms=five.filter(p=>!p.top);
            const topSlope=(tops.at(-1).v-tops[0].v)/(tops.at(-1).i-tops[0].i);
            const bottomSlope=(bottoms.at(-1).v-bottoms[0].v)/(bottoms.at(-1).i-bottoms[0].i);
            const span=e.i-a.i,scale=Math.max(...five.map(p=>p.v))-Math.min(...five.map(p=>p.v));
            const averageRim=(a.v+c.v+e.v)/3,tripleDepth=Math.abs(averageRim-(b.v+d.v)/2);
            if(tripleDepth>0&&Math.max(Math.abs(a.v-averageRim),Math.abs(c.v-averageRim),Math.abs(e.v-averageRim))<=tripleDepth*.15&&Math.abs(b.v-d.v)<tripleDepth*.3)
                emit(a.top?'Triple top':'Triple bottom',five,[b,d]);
            const flat=scale*.1/span;
            const fits=(points,slope)=>points.every(point=>Math.abs(point.v-(points[0].v+slope*(point.i-points[0].i)))<=scale*.12);
            const boundariesFit=fits(tops,topSlope)&&fits(bottoms,bottomSlope);
            const converges=(bottomSlope-topSlope)*span>scale*.2;
            if(boundariesFit&&scale>0&&converges&&topSlope>flat&&bottomSlope>flat)emit('Rising wedge',five);
            if(boundariesFit&&scale>0&&converges&&topSlope<-flat&&bottomSlope<-flat)emit('Falling wedge',five);
            if(boundariesFit&&scale>0&&Math.abs(topSlope)<=flat&&Math.abs(bottomSlope)<=flat)emit('Rectangle',five);
            // A flag/pennant needs an actual preceding pole, not just a channel.
            const before=run.slice(Math.max(0,a.i-span*2),a.i);
            if(before.length>=3){
                const extreme=a.top?Math.min(...before.map(row=>row[3])):Math.max(...before.map(row=>row[2]));
                const poleStart=before.find(row=>(a.top?row[3]:row[2])===extreme),poleHeight=Math.abs(a.v-extreme);
                const pole=[[poleStart[0],extreme],[a.t,a.v]];
                const extra={pole,start:poleStart[0]};
                if(boundariesFit&&poleHeight>scale*1.5){
                    if(a.top&&topSlope<0&&bottomSlope<0&&Math.abs(topSlope-bottomSlope)*span<scale*.25)emit('Bullish flag',five,[],extra);
                    if(!a.top&&topSlope>0&&bottomSlope>0&&Math.abs(topSlope-bottomSlope)*span<scale*.25)emit('Bearish flag',five,[],extra);
                    if(converges&&topSlope<=flat&&bottomSlope>=-flat)emit(a.top?'Bullish pennant':'Bearish pennant',five,[],extra);
                }
            }
            const cupSign=a.top?1:-1,rim=(a.v+c.v)/2,cupDepth=cupSign*(rim-b.v),handleDepth=cupSign*(rim-d.v);
            const cupBars=c.i-a.i,handleBars=e.i-c.i;
            const bottomCount=run.slice(a.i,c.i+1).filter(row=>cupSign*((a.top?row[3]:row[2])-b.v)<=cupDepth*.25).length;
            if(cupDepth>0&&cupBars>=6&&handleBars>=2&&handleBars<=cupBars*.65&&Math.abs(a.v-c.v)<cupDepth*.15&&handleDepth>cupDepth*.1&&handleDepth<cupDepth*.5&&cupSign*(e.v-rim)>=-cupDepth*.1&&(b.i-a.i)/cupBars>=.25&&(b.i-a.i)/cupBars<=.75&&bottomCount>=Math.max(2,cupBars*.15))
                emit(a.top?'Cup and handle':'Inverted cup and handle',five,[{t:a.t,v:rim},{t:e.t,v:rim}]);

            if(boundariesFit && scale>0 && topSlope<bottomSlope && (bottomSlope-topSlope)*span>scale*.2 && topSlope<=scale*.1/span && bottomSlope>=-scale*.1/span)
                emit(Math.abs(topSlope)*span<scale*.1?'Ascending triangle':Math.abs(bottomSlope)*span<scale*.1?'Descending triangle':'Symmetrical triangle',five);
        }
    }
    return { candles:rows.length, patterns };
}
